import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Edit, Clipboard, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const SurveyList = ({ surveys, onEdit, onView, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  if (!surveys || surveys.length === 0) {
    return (
      <div className="text-center text-gray-500 mt-8">
        <p>No surveys found.</p>
      </div>
    );
  }

  const handleCopyToClipboard = (survey) => {
    navigator.clipboard.writeText(JSON.stringify(survey)).then(() => {
      toast.success('Survey copied to clipboard!', {
        duration: 2000,
        position: 'bottom-right',
        style: {
          background: '#10B981',
          color: '#FFFFFF',
        },
        iconTheme: {
          primary: '#FFFFFF',
          secondary: '#10B981',
        },
      });
    }).catch(() => {
      toast.error('Failed to copy survey to clipboard', {
        duration: 2000,
        position: 'bottom-right',
      });
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <h2 className="text-2xl font-semibold text-gray-800">Surveys</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {surveys.map((survey) => (
          <motion.div
            key={survey.id}
            whileHover={{ scale: 1.03 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 overflow-hidden"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{survey.name}</h3>
              <p className="text-gray-600 mb-4">{survey.short_description}</p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {survey.questions ? survey.questions.length : 0} question{survey.questions && survey.questions.length !== 1 ? 's' : ''}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => onView(survey)}
                    className="p-2 text-primary-600 hover:text-primary-800 transition-colors duration-200"
                    aria-label="View survey"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => onEdit(survey)}
                    className="p-2 text-primary-600 hover:text-primary-800 transition-colors duration-200"
                    aria-label="Edit survey"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleCopyToClipboard(survey)}
                    className="p-2 text-primary-600 hover:text-primary-800 transition-colors duration-200"
                    aria-label="Copy survey to clipboard"
                  >
                    <Clipboard className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default SurveyList;